// JWT utility functions

// Ascending capability order of the plan ids the backend issues in the JWT.
// Unrecognised plans rank as free so gating fails closed.
const PLAN_RANK = { free: 0, visible: 1, optimized: 2, dominant: 3, enterprise: 4 };

/**
 * Whether a plan may create recurring report schedules (>= optimized).
 *
 * @param {string} plan - The user's subscription plan
 * @returns {boolean} True when the plan is optimized or higher
 */
export const canScheduleReports = (plan) => (PLAN_RANK[plan] ?? 0) >= PLAN_RANK.optimized;

/** User class representing authenticated user data */
export class User {
  constructor(email, id, name, subscriptionPlan, auditLimit, remainingAudits, subscriptionStatus) {
    this.email = email;
    this.id = id;
    this.name = name;
    this.subscriptionPlan = subscriptionPlan || "free";
    this.auditLimit = auditLimit || 0;
    this.remainingAudits = remainingAudits || 0;
    this.subscriptionStatus = subscriptionStatus || "none";
  }

  /**
   * Get display name (name if available, otherwise email)
   *
   * @returns {string} Display name
   */
  getDisplayName() {
    return this.name || this.email;
  }
}

/**
 * Decode a JWT token
 *
 * @param {string} token - JWT token
 * @returns {object | null} Decoded token payload or null if invalid
 */
export const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

/**
 * Check if a JWT token is expired
 *
 * @param {string} token - JWT token
 * @returns {boolean} True if expired, false otherwise
 */
export const isTokenExpired = (token) => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return true;
  }

  const currentTime = Date.now() / 1000;
  return decoded.exp < currentTime;
};

/**
 * Get user data from JWT token
 *
 * @param {string} token - JWT token
 * @returns {User | null} User instance or null if invalid/expired
 */
export const getUserFromToken = (token) => {
  if (!token) return null;

  if (isTokenExpired(token)) {
    // Token is expired, remove it
    localStorage.removeItem("accessToken");
    return null;
  }

  const decoded = decodeJWT(token);
  if (!decoded) return null;

  // Create User instance from token payload
  try {
    return new User(
      decoded.email,
      decoded.id,
      decoded.name,
      decoded.subscription_plan || "free",
      decoded.audit_limit || 0,
      decoded.remaining_audits || 0,
      decoded.subscription_status || "none",
    );
  } catch (error) {
    console.error("Error creating user from token:", error);
    return null;
  }
};

/**
 * Whether the current browser session has a valid, non-expired access token.
 *
 * @returns {boolean} True if a logged-in user is present
 */
export const isLoggedIn = () => !!getUserFromToken(localStorage.getItem("accessToken"));
